import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useToast } from '@/hooks/use-toast';
import apiClient from 'services/apiClient';
import tokenManager from 'services/auth/tokenManager';
import { setUserFromToken } from '../../../redux/slices/authSlice';
import { validatePhone, validateNmlsId, normalizePhone } from 'utils/validators.js';
import { formatPhoneNumber } from 'shared/forms/utils';
import { clearAuthStorage } from 'utils/storage.js';
import { buildAppPath, getCurrentOrgSlug, getTenantSessionIndicatorKey } from 'utils/tenant.js';
import layoutStyles from '../../../styles/features/onboarding-layout.module.css';
import Loader from '../../../components/shared/Loader';

const brandLogo = '/opus-wordmark-dark.png';

const redirectToBranchDomainIfNeeded = (responseData, fallbackPath = '/dashboard') => {
  if (typeof window === 'undefined') {
    return false;
  }

  const redirectUrl = responseData?.redirect_url;
  if (redirectUrl) {
    window.location.replace(redirectUrl);
    return true;
  }

  const branchSlug = (responseData?.branch_slug || responseData?.user?.branch_slug || '').trim();
  if (!branchSlug) {
    return false;
  }

  const currentHost = window.location.hostname || '';
  const hostLabels = currentHost.split('.').filter(Boolean);
  const firstLabel = (hostLabels[0] || '').toLowerCase();
  const branch = branchSlug.toLowerCase();

  // The backend composes a branch host as a SINGLE label, "branch-client"
  // (build_tenant_hostname / buildTenantUrl). Joining with a dot instead
  // produces a hostname that was never mapped — "op.opustest.local.test"
  // rather than "op-opustest.local.test" — and the browser simply cannot
  // resolve it.
  if (firstLabel === branch || firstLabel.startsWith(`${branch}-`)) {
    // Already on the branch host; nothing to do.
    return false;
  }

  const clientLabel = firstLabel;
  if (!clientLabel) {
    return false;
  }

  const baseLabels = hostLabels.slice(1);
  const baseHost = baseLabels.join('.');
  if (!baseHost) {
    return false;
  }

  const targetHost = `${branch}-${clientLabel}.${baseHost}`;
  const port = window.location.port ? `:${window.location.port}` : '';
  const protocol = window.location.protocol || 'https:';
  const normalizedPath = fallbackPath.startsWith('/') ? fallbackPath : `/${fallbackPath}`;
  const targetUrl = `${protocol}//${targetHost}${port}${normalizedPath}`;

  window.location.replace(targetUrl);
  return true;
};

const CompanyOnboarding = () => {
  const { toast } = useToast();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Log all URL parameters for debugging
  console.log('[CompanyOnboarding] URL Parameters:', Object.fromEntries(searchParams));

  // Stytch might use 'token' or 'stytch_token_type=magic_links' with 't' parameter
  const token =
    searchParams.get('token') ||
    searchParams.get('t') ||
    searchParams.get('session_token') ||
    searchParams.get('code') ||
    searchParams.get('invite_token') ||
    searchParams.get('invitation_token') ||
    (() => {
      // handle hash fragment token e.g. #token=abc123
      const hash = window.location.hash || '';
      const hashParams = new URLSearchParams(hash.replace('#', ''));
      return (
        hashParams.get('token') ||
        hashParams.get('session_token') ||
        hashParams.get('code') ||
        null
      );
    })();
  const org = searchParams.get('org'); // Organization subdomain for context
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [phoneError, setPhoneError] = useState('');
  const [invitationData, setInvitationData] = useState(null);

  // Debug logging for state changes
  useEffect(() => {
    console.log('[CompanyOnboarding] State updated:', {
      validating,
      invitationData,
      hasToken: !!token,
      currentStep
    });
  }, [validating, invitationData, token, currentStep]);
  const [isEmployeeOnboarding, setIsEmployeeOnboarding] = useState(false);

  // Use useRef to track validation status - persists across re-renders and React StrictMode
  // Store validation promise to prevent concurrent calls
  const validationPromise = useRef(null);
  const validationCompleted = useRef(false);
  const [formData, setFormData] = useState({
    phone: '',
    personalNmlsId: '',
    agreeToTerms: false
  });

  // UI-only carousel state
  const [currentSlide, setCurrentSlide] = useState(0);
  const [progressSeed, setProgressSeed] = useState(0);

  const employeeSlides = [
    {
      image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=2000&auto=format&fit=crop',
      title: 'Join our team and grow together.',
      highlight: 'grow',
    },
    {
      image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=2000&auto=format&fit=crop',
      title: 'Build your career with us.',
      highlight: 'career',
    },
    {
      image: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?q=80&w=2000&auto=format&fit=crop',
      title: 'Welcome to the team.',
      highlight: 'team',
    },
  ];

  useEffect(() => {
    // Check if this is employee onboarding from session
    const userData = tokenManager.getUserData();
    try {
      if (!import.meta.env.PROD) {
        console.log('[CompanyOnboarding] Initialization check:', {
          hasUserData: !!userData,
          hasToken: !!token,
          userDataPreview: userData ? {
            email: userData.email,
            role: userData.role,
            originalRole: userData.originalRole,
            invitation_status: userData.invitation_status,
            is_active: userData.is_active,
            needs_onboarding: userData.needs_onboarding,
            profile_completed: userData.profile_completed
          } : null
        });
      }

      if (userData && !token) {
        // User is authenticated and there's no token, could be employee or consumer onboarding
        const isEmployee = userData.role !== 'consumer';
        if (!import.meta.env.PROD) {
          console.log('[CompanyOnboarding] Session-based onboarding detected for user:', userData.email, 'role:', userData.role, 'isEmployee:', isEmployee);
        }
        setIsEmployeeOnboarding(isEmployee);

        // Set invitation data from authenticated user context
        const sessionInvitationData = {
          email: userData.email || userData.userEmail,
          first_name: userData.first_name || userData.firstName,
          last_name: userData.last_name || userData.lastName,
          role: userData.role,
          organization_name: userData.organization_name,
          organization_id: userData.organization_id || userData.organizationId,
          needs_nmls: ['loan_officer', 'branch_admin', 'client_admin'].includes(userData.role),
          employment_type: userData.employment_type,
          nmls_id: userData.personal_nmls_id || userData.nmls_id,
          phone_number: userData.phone_number,
          is_employee: isEmployee, // True for employees, false for consumers
          valid: true
        };

        if (!import.meta.env.PROD) {
          console.log('[CompanyOnboarding] Setting session invitation data for', isEmployee ? 'employee' : 'consumer', ':', sessionInvitationData);
        }

        setInvitationData(sessionInvitationData);
        setFormData(prev => ({
          ...prev,
          phone: userData.phone_number || prev.phone,
          personalNmlsId: userData.personal_nmls_id || userData.nmls_id || prev.personalNmlsId
        }));
        setValidating(false);
        return; // Skip token validation for session-based onboarding
      } else if (token) {
        // Token-based path — verifyInvitation() will handle setValidating(false)
        // Only clear session for invitation-based onboarding when no user data exists
        if (!userData) {
          clearAuthStorage();
        }
        // verifyInvitation is triggered by the second useEffect below
      } else {
        // Fix: no session user AND no recognizable token — prevent infinite spinner
        console.warn('[CompanyOnboarding] No user session and no invite token found in URL. Stopping loader.');
        setInvitationData({ valid: false, error: 'Your invitation link appears to be invalid or has expired. Please request a new invitation from your administrator.' });
        setValidating(false);
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 4000);
      }
    } catch (err) {
      console.error('[CompanyOnboarding] Bootstrap error:', err);
    } finally {
      // Safety net: if nothing else set validating=false for the no-token-no-user case,
      // the else branch above handles it. But if an unexpected exception fires, release spinner.
    }
  }, []);

  useEffect(() => {
    // Skip verification for session-based onboarding (both employees and consumers)
    const userData = tokenManager.getUserData();
    if (userData && !token) {
      if (!import.meta.env.PROD) {
        console.log('[CompanyOnboarding] Skipping token verification for session-based onboarding');
      }
      return;
    }

    // Verify invitation token only once - use promise to prevent concurrent calls
    // Font loading is now handled by CSS module
    if (token && !validationCompleted.current && !validationPromise.current) {
      // Store the promise to prevent concurrent calls
      validationPromise.current = verifyInvitation();
    }
  }, [token, isEmployeeOnboarding]); // Only depend on token - useRef doesn't need to be in dependencies

  // UI-only carousel auto-advance effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % employeeSlides.length);
      setProgressSeed((seed) => seed + 1);
    }, 6000);
    return () => clearTimeout(timer);
  }, [currentSlide]);

  const verifyInvitation = async () => {
    console.log('[CompanyOnboarding] verifyInvitation called with token:', token);
    if (!token) {
      console.error('[CompanyOnboarding] No token found in URL');
      toast({ title: 'Invalid invitation link - no token found', variant: "destructive" });
      navigate(buildAppPath('/login/b2b'));
      return;
    }

    // Prevent concurrent validation attempts
    if (validationCompleted.current) {
      if (!import.meta.env.PROD) {
        console.log('[CompanyOnboarding] Validation already completed, skipping duplicate call');
      }
      return;
    }

    // Mark as completed immediately to prevent double calls
    validationCompleted.current = true;

    // Add debug logging to track validation calls
    if (!import.meta.env.PROD) {
      console.log('[CompanyOnboarding] Starting token validation');
    }

    try {
      setValidating(true);
      // Call our public invitation validation endpoint
      // Organization context comes from the Stytch token itself
      const requestBody = { token: token };
      if (org) {
        requestBody.org = org; // Optional org parameter for future use
      }

      if (!import.meta.env.PROD) {
        console.log('[CompanyOnboarding] Making API call to validate invitation');
      }
      const response = await apiClient.post('/b2b/invitations/validate', requestBody);

      if (response.data.valid) {
        console.log('[CompanyOnboarding] Validation successful, setting invitation data:', response.data);

        // Set both states together to ensure atomic update
        setInvitationData(response.data);
        setFormData(prev => ({
          ...prev,
          phone: response.data.phone_number || prev.phone,
          personalNmlsId: response.data.nmls_id || prev.personalNmlsId
        }));
        setValidating(false);

        console.log('[CompanyOnboarding] States updated - should trigger re-render');

        toast({ title: `Welcome to ${response.data.organization_name}!` });
      } else {
        console.error('[CompanyOnboarding] Validation failed:', response.data.error);

        // Show error message but don't navigate away
        const errorMessage = response.data.error || 'Invalid invitation';

        // Set a user-friendly error state instead of navigating away
        setInvitationData({
          valid: false,
          error: errorMessage
        });

        toast({ title: errorMessage, variant: "destructive" });

        // Reset validation state on failure so user can retry with new link
        validationCompleted.current = false;
        validationPromise.current = null;
        setValidating(false); // Stop showing loading spinner

        // Only navigate away for truly invalid links, not for already-used ones
        if (!errorMessage.toLowerCase().includes('already been used')) {
          setTimeout(() => navigate(buildAppPath('/login/b2b')), 3000); // Give user time to read the error
        }
      }
    } catch (error) {
      if (!import.meta.env.PROD) {
        console.error('[CompanyOnboarding] Invitation validation error:', error);
      }

      // Check if this is a "token already used" error (typical double validation symptom)
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      if (errorMessage.toLowerCase().includes('already used') || errorMessage.toLowerCase().includes('expired')) {
        toast({ title: 'This invitation link has already been used or has expired. Please request a new invitation.', variant: "destructive" });
      } else {
        toast({ title: 'Invalid or expired invitation link', variant: "destructive" });
      }

      // Reset validation state on error so user can retry with new link
      validationCompleted.current = false;
      validationPromise.current = null;
      setValidating(false); // Stop showing loading spinner on error
      navigate(buildAppPath('/login/b2b'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.agreeToTerms) {
      toast({ title: 'Please agree to the terms and conditions', variant: "destructive" });
      return;
    }

    const phoneCheck = validatePhone(formData.phone);
    if (!phoneCheck.valid) {
      setPhoneError(phoneCheck.error);
      toast({ title: phoneCheck.error, variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      // Handle session-based onboarding (both employees and consumers)
      const userData = tokenManager.getUserData();
      if (userData && !token) {
        // For authenticated users (employees and consumers), update their profile with phone and NMLS info
        if (!userData?.id) {
          toast({ title: 'Unable to determine user context for onboarding', variant: "destructive" });
          setLoading(false);
          return;
        }

        const payload = {
          user_id: userData.id,
          phone_number: normalizePhone(formData.phone),
          personal_nmls_id: formData.personalNmlsId || undefined,
          employment_type: invitationData?.employment_type || undefined,
        };

        if (!import.meta.env.PROD) {
          console.log('[CompanyOnboarding] Session-based onboarding payload for', userData.role + ':', payload);
        }

        try {
          const response = await apiClient.post('/b2b/invitations/complete-onboarding', payload);

          if (!import.meta.env.PROD) {
            console.log('[CompanyOnboarding] Complete-onboarding response for', userData.role + ':', {
              success: response.data?.success,
              user: response.data?.user,
              message: response.data?.message
            });
          }

          if (response.data?.success) {
            const updatedUser = {
              ...userData,
              phone_number: formData.phone || userData.phone_number,
              personal_nmls_id: formData.personalNmlsId || userData.personal_nmls_id,
              employment_type: invitationData?.employment_type || userData.employment_type,
              profile_completed: true,
              needs_onboarding: false,
              invitation_status: 'accepted',
              is_active: true
            };

            if (response.data?.user) {
              Object.assign(updatedUser, {
                phone_number: response.data.user.phone_number ?? updatedUser.phone_number,
                personal_nmls_id: response.data.user.personal_nmls_id ?? updatedUser.personal_nmls_id,
                employment_type: response.data.user.employment_type ?? updatedUser.employment_type,
                invitation_status: response.data.user.invitation_status ?? updatedUser.invitation_status,
                is_active: response.data.user.is_active ?? updatedUser.is_active,
              });
            }

            tokenManager.setUserData(updatedUser);
            dispatch(setUserFromToken());

            toast({ title: response.data?.message || 'Onboarding complete!' });

            if (redirectToBranchDomainIfNeeded(response.data)) {
              return;
            }
            if (invitationData?.role === 'loan_officer') {
              navigate(buildAppPath('/loanInformation'), { replace: true });
            } else if (invitationData?.role === 'loan_processor' || invitationData?.role === 'processor') {
              navigate(buildAppPath('/pipeline'), { replace: true });
            } else if (invitationData?.role === 'consumer') {
              navigate(buildAppPath('/dashboard'), { replace: true }); // Consumer dashboard (B2C)
            } else {
              navigate(buildAppPath('/dashboard'), { replace: true }); // Default dashboard
            }

            setLoading(false);
          } else {
            toast({ title: response.data?.message || 'Failed to complete onboarding', variant: "destructive" });
          }
        } catch (apiError) {
          console.error('[CompanyOnboarding] Failed to complete onboarding', apiError);
          toast({ title: apiError.response?.data?.detail || 'Failed to complete onboarding', variant: "destructive" });
        }

        return;
      }

      // Original invitation acceptance flow for client admins
      const acceptRequest = {
        phone_number: normalizePhone(formData.phone),
        personal_nmls_id: formData.personalNmlsId
      };

      // With cookie-based auth, the session is already set via HttpOnly cookie
      // We just need to pass the member and organization IDs for reference
      if (invitationData?.member_id) {
        acceptRequest.member_id = invitationData.member_id;
        acceptRequest.organization_id = invitationData.organization_id;
      }

      const response = await apiClient.post('/b2b/invitations/accept', acceptRequest);

      if (response.data.success) {
        // Backend now sets HttpOnly cookies - no need to store tokens
        // Just store user data if provided
        if (response.data.user) {
          tokenManager.setUserData(response.data.user);
        }

        // Store organization ID for reference (if needed)
        if (response.data.organization_id) {
          // User data will be stored via tokenManager.setUserData() if provided above
        }

        // Show success message from backend or default message
        const successMessage = response.data.message || 'Onboarding complete! Redirecting to dashboard...';
        toast({ title: successMessage });

        // Update token manager with completed onboarding state
        const updatedUser = {
          ...tokenManager.getUserData(),
          user_id: response.data.user_id,
          organization_id: response.data.organization_id,
          profile_completed: true,
          needs_onboarding: false,
          invitation_status: 'accepted',
          is_active: true,
        };
        if (response.data.user) {
          Object.assign(updatedUser, response.data.user);
        }
        tokenManager.setUserData(updatedUser);
        dispatch(setUserFromToken());

        // Navigate to the appropriate dashboard based on role
        if (redirectToBranchDomainIfNeeded(response.data)) {
          return;
        }
        if (invitationData?.role === 'loan_officer') {
          navigate(buildAppPath('/loanInformation'), { replace: true });
        } else if (invitationData?.role === 'loan_processor' || invitationData?.role === 'processor') {
          navigate(buildAppPath('/pipeline'), { replace: true });
        } else {
          navigate(buildAppPath('/dashboard'), { replace: true });
        }
      } else {
        toast({ title: response.data.error || 'Failed to complete onboarding', variant: "destructive" });
      }
    } catch (error) {
      if (!import.meta.env.PROD) {
        console.error('Onboarding error');
      }
      toast({ title: error.response?.data?.detail || 'Failed to complete onboarding', variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Mirror personal-info: format as US number only once all 10 digits are present.
  const formatPhoneForDisplay = (value) => {
    const digits = ('' + value).replace(/\D/g, '');
    return digits.length === 10 ? formatPhoneNumber(digits, '(###) ###-####') : digits;
  };

  // Log the current render state
  console.log('[CompanyOnboarding] Render check:', {
    validating,
    hasInvitationData: !!invitationData,
    invitationDataValid: invitationData?.valid,
    invitationData: invitationData,
    willShowLoading: validating,
    willShowError: !validating && (!invitationData || invitationData.valid === false),
    willShowForm: !validating && invitationData && invitationData.valid !== false
  });

  if (validating) {
    return <Loader variant="colorful" FullScreen={true} message="Loading your onboarding..." />;
  }

  if (!invitationData) {
    console.error('[CompanyOnboarding] No invitation data available after validation');
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem' }}>No invitation data available. Please request a new invitation link.</p>
      </div>
    );
  }

  const renderSlideTitle = (slide) => {
    const parts = slide.title.split(slide.highlight);
    if (parts.length === 1) return slide.title;
    return (
      <>
        {parts[0]}
        <span className={layoutStyles.slideHighlight}>{slide.highlight}</span>
        {parts.slice(1).join(slide.highlight)}
      </>
    );
  };

  // Read user role from existing invitation state
  const backendRole = invitationData?.role || '';
  const needsNmls = invitationData?.needs_nmls || ['loan_officer', 'branch_admin', 'client_admin'].includes(invitationData?.role);

  return (
    <div className={layoutStyles.root}>
      <div className={layoutStyles.carouselPane}>
        <div className={layoutStyles.carouselInner}>
          {employeeSlides.map((slide, index) => (
            <div
              key={index}
              className={`${layoutStyles.slide} ${index === currentSlide ? layoutStyles.slideActive : layoutStyles.slideInactive}`}
            >
              <div className={layoutStyles.slideOverlay} />
              <div className={layoutStyles.carouselBlur} />
              <img src={slide.image} alt={slide.title} className={layoutStyles.slideImage} />
              <div className={layoutStyles.slideText}>
                <h2 className={layoutStyles.slideTitle}>{renderSlideTitle(slide)}</h2>
              </div>
            </div>
          ))}
          <div className={layoutStyles.dots}>
            {employeeSlides.map((_, index) => (
              <button
                key={index}
                type="button"
                aria-label={`Go to slide ${index + 1}`}
                onClick={() => { setCurrentSlide(index); setProgressSeed((seed) => seed + 1); }}
                className={`${layoutStyles.dotButton} ${index === currentSlide ? layoutStyles.dotActive : layoutStyles.dotInactive}`}
              >
                {index === currentSlide && (
                  <span className={layoutStyles.dotProgress} key={`progress-${index}-${progressSeed}`} />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className={layoutStyles.rightPane}>
        <div className={layoutStyles.contentPane}>
          <div className={layoutStyles.card}>
            <div className={layoutStyles.pageContent}>
              <div className={`${layoutStyles.formContainer} ${layoutStyles.fadeInContent}`}>

                <div className={layoutStyles.logoContainer}>
                  <img
                    src={brandLogo}
                    alt="Opus Capital Markets Consultants"
                    className={layoutStyles.logoIcon}
                  />
                </div>

                <div className={layoutStyles.progressContainer}>
                  <div className={layoutStyles.progressBar} />
                </div>

                <h1 className={layoutStyles.heading}>Complete your employee profile</h1>
                <p className={layoutStyles.subheading}>Please confirm your contact details to finish onboarding.</p>

                {invitationData?.valid === false && (
                  <div className={layoutStyles.errorAlert}>{invitationData.error || 'Invalid invitation'}</div>
                )}

                {/* Keep the existing form onSubmit handler exactly as-is */}
                <form onSubmit={handleSubmit} className={layoutStyles.formStack}>

                  <div className={layoutStyles.inputGroup}>
                    <label className={layoutStyles.label}>Email</label>
                    <input
                      type="email"
                      value={invitationData?.email || ''}
                      readOnly
                      className={`${layoutStyles.input} ${layoutStyles.inputMuted}`}
                    />
                  </div>

                  <div className={layoutStyles.inputRow}>
                    <div className={layoutStyles.inputGroup}>
                      <label className={layoutStyles.label}>First Name</label>
                      <input
                        type="text"
                        value={invitationData?.first_name || ''}
                        readOnly
                        className={`${layoutStyles.input} ${layoutStyles.inputMuted}`}
                      />
                    </div>
                    <div className={layoutStyles.inputGroup}>
                      <label className={layoutStyles.label}>Last Name</label>
                      <input
                        type="text"
                        value={invitationData?.last_name || ''}
                        readOnly
                        className={`${layoutStyles.input} ${layoutStyles.inputMuted}`}
                      />
                    </div>
                  </div>

                  <div className={layoutStyles.inputGroup}>
                    <label className={layoutStyles.label}>Role</label>
                    <input
                      type="text"
                      value={backendRole}
                      readOnly
                      className={`${layoutStyles.input} ${layoutStyles.inputMuted}`}
                    />
                  </div>

                  {/* Phone — bind to existing formData.phone state */}
                  <div className={layoutStyles.inputGroup}>
                    <label className={layoutStyles.label}>Mobile Number</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => {
                        handleInputChange('phone', formatPhoneForDisplay(e.target.value));
                        if (phoneError) setPhoneError('');
                      }}
                      onBlur={() => {
                        const check = validatePhone(formData.phone);
                        setPhoneError(check.valid ? '' : check.error);
                      }}
                      className={layoutStyles.input}
                      placeholder="Enter your mobile number"
                    />
                    {phoneError && (
                      <span className={layoutStyles.errorMessage}>{phoneError}</span>
                    )}
                  </div>

                  {/* NMLS ID — required for LOs, branch admins, client admins */}
                  {needsNmls && (
                    <div className={layoutStyles.inputGroup}>
                      <label className={layoutStyles.label}>Personal NMLS ID</label>
                      <input
                        type="text"
                        value={formData.personalNmlsId}
                        onChange={(e) => handleInputChange('personalNmlsId', e.target.value)}
                        className={layoutStyles.input}
                        placeholder="Enter your NMLS ID (optional)"
                      />
                    </div>
                  )}

                  {/* Terms and conditions checkbox */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginTop: '4px' }}>
                    <input
                      type="checkbox"
                      id="agreeToTerms"
                      checked={formData.agreeToTerms}
                      onChange={(e) => handleInputChange('agreeToTerms', e.target.checked)}
                      style={{ marginTop: '2px', cursor: 'pointer', accentColor: '#3c588b' }}
                    />
                    <label htmlFor="agreeToTerms" className={layoutStyles.label} style={{ marginBottom: 0, fontWeight: 400, cursor: 'pointer' }}>
                      I agree to the Terms of Service and Privacy Policy, and acknowledge that MIRA will process my data in accordance with its privacy practices.
                    </label>
                  </div>

                  <div className={layoutStyles.buttonContainer}>
                    <button
                      type="submit"
                      disabled={loading || !formData.agreeToTerms || !validatePhone(formData.phone).valid}
                      className={`${layoutStyles.button} ${layoutStyles.buttonPrimary} ${layoutStyles.buttonFull}`}
                    >
                      {loading ? 'Completing onboarding...' : 'Complete Onboarding'}
                    </button>
                  </div>

                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyOnboarding;
